import React, { useRef, useEffect } from 'react';
import ImageUploadModal from './ImageUploadModal';
import ImageEditor from './ImageEditor';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Link, 
  Image as ImageIcon,
  Code,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Eye
} from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = "Start writing your content..." }) => {
  const editorRef = useRef(null);
  const [showPreview, setShowPreview] = React.useState(false);
  const [showImageModal, setShowImageModal] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [showImageEditor, setShowImageEditor] = React.useState(false);

  // Update editor content when value changes (for edit mode)
  useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Add click listener for images
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        setSelectedImage({
          element: e.target,
          src: e.target.src,
          width: e.target.width || 300,
          height: e.target.height || 'auto',
          align: e.target.style.float || 'left'
        });
        setShowImageEditor(true);
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('click', handleImageClick);
      return () => editor.removeEventListener('click', handleImageClick);
    }
  }, []);

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    updateContent();
  };

  const updateContent = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  };

  const insertImage = (url) => {
    if (url) {
      // Insert image with default styling
      const img = document.createElement('img');
      img.src = url;
      img.style.width = '400px';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = '0.5rem';
      img.style.margin = '1rem 0';
      img.style.cursor = 'pointer';
      img.title = 'Click to edit image';
      
      if (editorRef.current) {
        editorRef.current.appendChild(img);
        updateContent();
      }
      setShowImageModal(false);
    }
  };

  const handleImageUpdate = (updates) => {
    if (selectedImage && selectedImage.element) {
      const img = selectedImage.element;
      img.style.width = `${updates.width}px`;
      img.style.maxWidth = '100%';
      
      if (updates.alignment === 'center') {
        img.style.display = 'block';
        img.style.margin = '1rem auto';
        img.style.float = 'none';
      } else {
        img.style.display = 'inline';
        img.style.float = updates.alignment;
        img.style.margin = updates.alignment === 'left' ? '0 1rem 1rem 0' : '0 0 1rem 1rem';
      }
      
      updateContent();
    }
  };

  const handleImageDelete = () => {
    if (selectedImage && selectedImage.element) {
      selectedImage.element.remove();
      updateContent();
      setShowImageEditor(false);
      setSelectedImage(null);
    }
  };

  const formatHeading = (level) => {
    executeCommand('formatBlock', `<h${level}>`);
  };

  const toolbarButtons = [
    { icon: Bold, command: 'bold', title: 'Bold (Ctrl+B)' },
    { icon: Italic, command: 'italic', title: 'Italic (Ctrl+I)' },
    { icon: Underline, command: 'underline', title: 'Underline (Ctrl+U)' },
    { icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
    { icon: ListOrdered, command: 'insertOrderedList', title: 'Numbered List' },
    { icon: AlignLeft, command: 'justifyLeft', title: 'Align Left' },
    { icon: AlignCenter, command: 'justifyCenter', title: 'Align Center' },
    { icon: AlignRight, command: 'justifyRight', title: 'Align Right' },
  ];

  return (
    <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1">
        {/* Headings */}
        <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            type="button"
            onClick={() => formatHeading(2)}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => formatHeading(3)}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </button>
        </div>

        {/* Text Formatting */}
        <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
          {toolbarButtons.slice(0, 3).map((btn, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => executeCommand(btn.command)}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title={btn.title}
            >
              <btn.icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        {/* Lists */}
        <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
          {toolbarButtons.slice(3, 5).map((btn, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => executeCommand(btn.command)}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title={btn.title}
            >
              <btn.icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        {/* Alignment */}
        <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
          {toolbarButtons.slice(5, 8).map((btn, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => executeCommand(btn.command)}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title={btn.title}
            >
              <btn.icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        {/* Insert */}
        <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            type="button"
            onClick={insertLink}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Insert Link"
          >
            <Link className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setShowImageModal(true)}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Insert Image"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => executeCommand('removeFormat')}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Clear Formatting"
          >
            <Code className="w-4 h-4" />
          </button>
        </div>

        {/* Preview Toggle */}
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`p-2 rounded transition-colors ml-auto ${
            showPreview ? 'bg-primary text-white' : 'hover:bg-gray-200'
          }`}
          title="Toggle Preview"
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>

      {/* Editor/Preview */}
      {showPreview ? (
        <div 
          className="p-4 min-h-[400px] max-h-[600px] overflow-y-auto prose max-w-none"
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={updateContent}
          onBlur={updateContent}
          className="p-4 min-h-[400px] max-h-[600px] overflow-y-auto focus:outline-none prose max-w-none"
          style={{ minHeight: '400px' }}
          data-placeholder={placeholder}
        />
      )}

      {/* Character Count */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-sm text-gray-600 flex justify-between items-center">
        <span>
          {value ? `${value.replace(/<[^>]*>/g, '').length} characters` : '0 characters'}
        </span>
        <span className="text-xs text-gray-500">
          {showPreview ? 'Preview Mode' : 'Edit Mode'}
        </span>
      </div>

      {/* CSS for placeholder */}
      <style>
        {`
          [contentEditable]:empty:before {
            content: attr(data-placeholder);
            color: #9CA3AF;
            pointer-events: none;
          }
          .prose h2 { font-size: 1.875rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; }
          .prose h3 { font-size: 1.5rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; }
          .prose p { margin-bottom: 1rem; line-height: 1.75; }
          .prose ul, .prose ol { margin-bottom: 1rem; padding-left: 1.5rem; }
          .prose li { margin-bottom: 0.5rem; }
          .prose a { color: #1e3a8a; text-decoration: underline; }
          .prose img { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 1rem 0; cursor: pointer; transition: transform 0.2s; }
          .prose img:hover { transform: scale(1.02); box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
          .prose strong { font-weight: 700; }
          .prose em { font-style: italic; }
        `}
      </style>

      {/* Image Upload Modal */}
      {showImageModal && (
        <ImageUploadModal
          onInsert={insertImage}
          onClose={() => setShowImageModal(false)}
        />
      )}

      {/* Image Editor Modal */}
      {showImageEditor && selectedImage && (
        <ImageEditor
          image={selectedImage}
          onUpdate={handleImageUpdate}
          onDelete={handleImageDelete}
          onClose={() => {
            setShowImageEditor(false);
            setSelectedImage(null);
          }}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
